import { useMe, useProducts } from '@studio/api-client';
import type { UniversalProduct } from '@studio/contract';
import { Button, Card, CardBody, CardDescription, CardHeader, CardTitle, Chip } from '@studio/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandDnaModal } from '../components/BrandDnaModal';
import { humanizeError } from '../lib/error-copy';
import { getWixMediaSafeUrl } from '../lib/wix-cdn';

export function ProductsPage() {
  const me = useMe();
  const products = useProducts({ enabled: me.isSuccess && me.data.workspaceId !== null });
  const [brandOpen, setBrandOpen] = useState(false);
  const navigate = useNavigate();

  function handlePick(product: UniversalProduct) {
    navigate(`/studio/${encodeURIComponent(product.uid)}`);
  }

  return (
    <AppShell activeTab="products" onOpenBrand={() => setBrandOpen(true)}>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <p className="eyebrow mb-2">Surface 1 · Catalog</p>
          <h1 className="display text-display-xl">Your products</h1>
          <p className="mt-1 text-sm text-fg-3">
            Click a product to open Studio and generate variations.
          </p>
        </div>
      </header>

      <ProductsBody me={me} products={products} onPick={handlePick} />
      <BrandDnaModal open={brandOpen} onOpenChange={setBrandOpen} />
    </AppShell>
  );
}

interface ProductsBodyProps {
  me: ReturnType<typeof useMe>;
  products: ReturnType<typeof useProducts>;
  onPick: (product: UniversalProduct) => void;
}

function ProductsBody({ me, products, onPick }: ProductsBodyProps) {
  if (me.isPending) {
    return <ProductSkeletons />;
  }
  if (me.error) {
    return <ErrorState message={humanizeError(me.error)} />;
  }
  if (me.data && me.data.workspaceId === null) {
    return (
      <EmptyState
        title="Workspace not ready yet"
        body="The backend needs a workspace bound to your Clerk user. Sign out + back in once the bootstrap lands so the new JWT carries `activeWorkspaceId`."
        cta={{ to: '/sign-in', label: 'Sign in again' }}
      />
    );
  }
  if (products.isPending) {
    return <ProductSkeletons />;
  }
  if (products.error) {
    return <ErrorState message={humanizeError(products.error)} />;
  }
  if (!products.data || products.data.items.length === 0) {
    return (
      <EmptyState
        title="No products yet"
        body="Connect Wix, Etsy, or eBay to import your catalog. Studio will read from here."
        cta={{ to: '/connections', label: 'Connect a platform' }}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {products.data.items.map((p) => (
        <button
          key={p.uid}
          type="button"
          onClick={() => onPick(p)}
          className="text-left focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-lg"
          aria-label={`Open studio for ${p.title}`}
        >
          <Card interactive className="overflow-hidden">
            {p.primaryImageUrl ? (
              <img
                src={getWixMediaSafeUrl(p.primaryImageUrl)}
                alt=""
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full bg-surface-2 object-cover"
              />
            ) : (
              <div className="aspect-[4/5] w-full bg-surface-2" aria-hidden />
            )}
            <CardHeader>
              <CardTitle className="line-clamp-1 text-sm">{p.title}</CardTitle>
              <CardDescription className="flex items-center gap-2 text-xs">
                <Chip size="xs" variant="soft" tone="accent">
                  {p.platformId}
                </Chip>
                <Chip size="xs" variant="solid" tone="neutral">
                  {p.status}
                </Chip>
              </CardDescription>
            </CardHeader>
            <CardBody className="pt-0 text-xs text-fg-3">
              {p.imageCount} images · {p.variantCount} variants
            </CardBody>
          </Card>
        </button>
      ))}
    </div>
  );
}

function ProductSkeletons() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      aria-busy="true"
      aria-label="Loading products"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: pure presentational skeleton, order is fixed
        <div key={i} className="overflow-hidden rounded-lg border border-border bg-surface">
          <div className="aspect-[4/5] w-full animate-pulse bg-surface-2" />
          <div className="space-y-2 p-4">
            <div className="h-3 w-3/4 animate-pulse rounded bg-surface-2" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-surface-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-danger/40 bg-danger/10 p-5 text-sm text-danger">
      {message}
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  body: string;
  cta?: { to: string; label: string };
}

function EmptyState({ title, body, cta }: EmptyStateProps) {
  return (
    <div className="rounded-lg border border-border bg-surface p-8 text-center">
      <h2 className="display text-display-md">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-fg-2">{body}</p>
      {cta ? (
        <div className="mt-5">
          <Button asChild variant="primary">
            <Link to={cta.to}>{cta.label}</Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
